import React from "react";
import {
  AppBar,
  Avatar,
  Box,
  IconButton,
  Toolbar,
  Typography,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";

import { Session } from "utils";

import MobileNavDrawer from "./MobileNavDrawer";

/**
 * MobileAppShell — the shared chrome for every mobile screen: a sticky top bar
 * (hamburger + brand logo on the left, current page title on the right) and the
 * slide-in nav drawer. Screens render their content as `children` in the
 * scrollable area below the bar.
 *
 * An optional `footer` is pinned to the bottom of the viewport (below the
 * scrollable area) — used by the interview screen for the question-pills strip.
 *
 * `hideMenu` removes the hamburger + nav drawer entirely — used by the
 * assessment screen, where the only way out is the End Session button (no
 * navigating to Home/Performance mid-attempt).
 */
export default function MobileAppShell({
  title = "",
  children,
  footer = null,
  hideMenu = false,
}) {
  const [drawerOpen, setDrawerOpen] = React.useState(false);

  const { logoUrl } = Session?.getClientMetadata?.() || {};

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        width: "100%",
        bgcolor: "shades.clr-offWhite",
        overflow: "hidden",
      }}
    >
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: "shades.clr-white-900",
          borderBottom: "1px solid",
          borderColor: "neutral.clr-100",
        }}
      >
        <Toolbar sx={{ gap: 2, minHeight: 56 }}>
          {!hideMenu && (
            <IconButton
              edge="start"
              onClick={() => setDrawerOpen(true)}
              aria-label="open menu"
              sx={{ color: "neutral.clr-800" }}
            >
              <MenuIcon />
            </IconButton>
          )}

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1 }}>
            {logoUrl ? (
              <Box
                component="img"
                src={logoUrl}
                alt="logo"
                sx={{ height: 24, maxWidth: 120, objectFit: "contain" }}
              />
            ) : (
              <>
                <Avatar
                  sx={{ bgcolor: "warning.clr-300", width: 28, height: 28 }}
                >
                  <Typography variant="body02-bold" color="shades.clr-white-900">
                    YL
                  </Typography>
                </Avatar>
                <Typography variant="body01-semiBold" color="neutral.clr-800">
                  YourLogo
                </Typography>
              </>
            )}
          </Box>

          {title && (
            <Typography variant="body01-bold" color="neutral.clr-900">
              {title}
            </Typography>
          )}
        </Toolbar>
      </AppBar>

      <Box
        component="main"
        sx={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          WebkitOverflowScrolling: "touch",
        }}
      >
        {children}
      </Box>

      {footer && <Box sx={{ flexShrink: 0 }}>{footer}</Box>}

      {!hideMenu && (
        <MobileNavDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
      )}
    </Box>
  );
}
